import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { AuthSessionContext } from '@context/AuthContext'
import { useModal } from '@context/ModalsContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body2 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { IconButton } from '@ui/components/Button'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { InfoIcon } from '@ui/icons/Info.icon'
import { ReplayIcon } from '@ui/icons/Replay.icon'
import { TrashIcon } from '@ui/icons/Trash.icon'

import { isDocumentManagement2MonthExpired } from '@utils/AccountAccess/accountDocumentStatuses'
import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { formatDate } from '@utils/date.utils'
import { CommonPaths } from '@utils/path'

import {
  isDocumentCategoryAdditionalType,
  isDocumentCategoryAddressType,
  isDocumentCategoryAppropriatenessTestType,
  isDocumentCategoryPaymentsType,
  isDocumentCategoryPersonalType,
} from '@model/DocumentCategories'
import {
  DocumentClientAreaDto,
  isDocumentClientAreaDeletedStatus,
  isDocumentClientAreaExpiredStatus,
} from '@model/DocumentClientAreaDto'

import { useScrollToElementIds } from '@hooks/useScrollToElementIds'

import { ConfirmModal } from '@shared/modals/Confirm.modal'

import { DocumentBadgeStatus } from './DocumentBadgeStatus'
import { UploadedDocumentStatusHistoryModal } from './UploadedDocumentStatusHistory'

import styles from './UploadedDocumentCard.module.scss'

interface Props {
  documents: DocumentClientAreaDto[]
  forceRefresh(): void
  readOnlyDocumentIds: string[]
}

export const UploadedDocumentsCards: React.FC<Props> = (props) => {
  const { documents = [], forceRefresh, readOnlyDocumentIds } = props

  return (
    <div className={styles.documentCards}>
      {documents.map((x, index) => (
        <UploadedDocumentCard
          document={x}
          forceRefresh={forceRefresh}
          readOnlyDocumentIds={readOnlyDocumentIds}
          cardId={`uploaded-document-card-${index + 1}`}
          key={`uploadedDocumentCard-${index}`}
        />
      ))}
    </div>
  )
}

interface CardProps {
  document: DocumentClientAreaDto
  forceRefresh(): void
  readOnlyDocumentIds: string[]
  cardId: string
}

const UploadedDocumentCard: React.FC<CardProps> = (props) => {
  const { document, forceRefresh, cardId } = props

  const { t } = useTranslation()
  const { scrollIntoView } = useScrollToElementIds()
  const navigate = useNavigate()
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()

  const [auth] = useContext(AuthSessionContext)
  const dateFormat = auth?.dateFormatType?.name

  const [isExpanded, setIsExpanded] = useState(false)
  const statusHistoryModal = useModal(UploadedDocumentStatusHistoryModal)

  const deleteConfirmModal = useModal(ConfirmModal)

  const goTo = (path: string) => {
    navigate(path, {
      state: {
        document,
        documentCategory: document.category,
        documentCategoryType: document.type,
      },
    })
  }

  const handleDocumentRenew = () => {
    if (document) {
      const { id } = document.category
      if (isDocumentCategoryPersonalType(id)) {
        goTo(`${CommonPaths.DocumentManagement}/identification`)
      } else if (isDocumentCategoryAddressType(id)) {
        goTo(`${CommonPaths.DocumentManagement}/proof-of-address`)
      } else if (isDocumentCategoryAppropriatenessTestType(id)) {
        goTo(`${CommonPaths.DocumentManagement}/company-proof`)
      } else if (isDocumentCategoryAdditionalType(id)) {
        goTo(`${CommonPaths.DocumentManagement}/additional`)
      } else if (isDocumentCategoryPaymentsType(id)) {
        goTo(`${CommonPaths.DocumentManagement}/payments`)
      }
    }
  }

  const handleDocumentDelete = async () => {
    if (document) {
      try {
        await apiClient.deleteDocument(document?.id)
        await queryClient.invalidateQueries({ queryKey: ['document'] })
      } finally {
        forceRefresh()
      }
    }
  }

  const handleIsExpandedOnClick = (cardId: string) => {
    if (!isExpanded) {
      scrollIntoView([cardId])
    }
    setIsExpanded((prev) => !prev)
  }

  const handleConfirmationModalOpen = () => {
    deleteConfirmModal.open({
      title: t('Profile.Uploaded Document'),
      description: t('Profile.Do you really want to delete this document?'),
      secondaryLabel: t('Cancel'),
      primaryLabel: t('Confirm'),
      onConfirm: handleDocumentDelete,
    })
  }

  const stopEventPropagationCallback = (callback?: () => void) => (event: React.MouseEvent) => {
    event.stopPropagation()
    callback?.()
  }

  return (
    <div className={styles.card} onClick={() => handleIsExpandedOnClick(cardId)} id={cardId}>
      {!isExpanded && (
        <>
          <div className={styles.title}>
            <Body2 medium>
              <SensitiveData>{document.type.name}</SensitiveData>
            </Body2>
            <DocumentBadgeStatus {...document.status} />
          </div>
          <div className={styles.subtitle}>
            <span className={styles.secondary}>{t('Profile.Upload date')}</span>
            <span>
              <SensitiveData>
                {formatDate(document.uploadedDate, { formatType: dateFormat })}
              </SensitiveData>
            </span>
          </div>
        </>
      )}
      {isExpanded && (
        <>
          <div className={styles.title}>
            <Body2 medium>
              <SensitiveData>{document.type.name}</SensitiveData>
            </Body2>
          </div>
          <div className={styles.subtitle}>
            <span className={styles.secondary}>{t('Profile.Document Name')}</span>
            <span
              className={classNames(styles.contrastMax, styles.trimmedName)}
              onClick={stopEventPropagationCallback()}
            >
              <SensitiveData>{document.name}</SensitiveData>
            </span>
          </div>
          <div className={styles.subtitle}>
            <span className={styles.secondary}>{t('Profile.Upload date')}</span>
            <span>
              <SensitiveData>
                {formatDate(document.uploadedDate, { formatType: dateFormat })}
              </SensitiveData>
            </span>
          </div>
          <div className={styles.subtitle}>
            <span className={classNames('is-flex is-flex-direction-row', styles.secondary)}>
              {t('Profile.Expiry date')}
              {!isDocumentClientAreaExpiredStatus(document) && (
                <ExpiryWarning document={document} onClick={stopEventPropagationCallback()} />
              )}
            </span>
            <span
              className={classNames({
                [styles.error]: isDocumentClientAreaExpiredStatus(document),
              })}
            >
              {document.expiryDate && formatDate(document.expiryDate, { formatType: dateFormat })}
            </span>
          </div>
          <div className={styles.subtitle}>
            <span className={styles.secondary}> {t('Profile.Status')}</span>
            <DocumentBadgeStatus
              {...document.status}
              onClick={() => statusHistoryModal.open({ documentClientArea: document })}
            />
          </div>
          <div className={classNames(styles.footer, 'mt-2')}>
            <IconButton.Ghost
              icon={ReplayIcon}
              onClick={stopEventPropagationCallback(handleDocumentRenew)}
            />
            {!isDocumentClientAreaDeletedStatus(document) && (
              <IconButton.Ghost
                icon={TrashIcon}
                onClick={stopEventPropagationCallback(handleConfirmationModalOpen)}
              />
            )}
          </div>
        </>
      )}
    </div>
  )
}

interface ExpiryWarningProps {
  document: DocumentClientAreaDto
  onClick?: React.MouseEventHandler<HTMLDivElement>
}

export const ExpiryWarning: React.FC<ExpiryWarningProps> = ({ document, onClick }) => {
  const { expiryDate } = document
  const { t } = useTranslation()

  if (!expiryDate) {
    return null
  }

  if (isDocumentManagement2MonthExpired(expiryDate)) {
    return (
      <div className={classNames('is-flex is-align-items-center pl-2')} onClick={onClick}>
        <Tooltip autoHide={2000} content={t('Expires soon')} direction={'topRight'}>
          <ActionIcon icon={InfoIcon} size='x20' />
        </Tooltip>
      </div>
    )
  }

  return null
}
